<script setup>
/**
 * One approved WhatsApp message, offered or refused.
 *
 * IT SHOWS THE WHOLE BODY, not a name. The owner did not write these names — Meta's
 * approval flow did, and `eid_offer_v2_final` tells her nothing about what her customers
 * would read. The body is the choice; the name is a footnote.
 *
 * AN UNSENDABLE TEMPLATE IS SHOWN, GREYED, WITH ITS REASON. Hiding it would leave an owner
 * hunting for the message she can see in WhatsApp and cannot find here. The reason is the
 * BFF's sentence, verbatim — the same sentence the submit-time gate answers with, so
 * somebody who gets past the picker is never told a second, different story.
 *
 * The card is the `RadioCard` anatomy the Answers dialog and the Personality style
 * selector already use (`outline-n-blue-9` when chosen, `outline-n-weak` otherwise), but
 * written out rather than imported: `RadioCard` renders its own `<input type="radio">`,
 * which R7 exists to keep out of this tree, and its label/description pair has no room for
 * a four-line message body.
 */
import { computed } from 'vue';

import { useWasloI18n } from '../../i18n.js';

const props = defineProps({
  /** A row from `wasloApi.getMessageTemplates`. */
  template: { type: Object, required: true },
  isChosen: { type: Boolean, default: false },
});

const emit = defineEmits(['choose']);

const { t } = useWasloI18n();

const blanks = computed(() =>
  props.template.variables.length === 0
    ? t('WASLO.BROADCASTS.COMPOSE.MESSAGE.NO_BLANKS')
    : t('WASLO.BROADCASTS.COMPOSE.MESSAGE.BLANKS', {
        count: props.template.variables.length,
      })
);

// WhatsApp bills a marketing template differently from a utility one, and it is the only
// thing on this card that changes what the send costs — so it is the only category worth
// a chip. Everything else is silence.
const isMarketing = computed(
  () => props.template.category.toUpperCase() === 'MARKETING'
);

const choose = () => {
  if (props.template.sendable && !props.isChosen) {
    emit('choose', props.template);
  }
};
</script>

<template>
  <div
    class="flex flex-col gap-3 p-4 outline outline-1 -outline-offset-1 rounded-xl bg-n-solid-1"
    :class="
      isChosen
        ? 'outline-n-blue-9'
        : template.sendable
          ? 'outline-n-weak'
          : 'outline-n-weak opacity-70'
    "
    :data-waslo-template="template.name"
    :data-waslo-template-chosen="isChosen"
  >
    <!--
      The approved text is Meta's, and it is usually Arabic. `dir="auto"` on the paragraph
      is right HERE and wrong on a list row: this block holds nothing but the message, so
      there is no frame for an RTL run to drag across.
    -->
    <p
      class="mb-0 text-sm leading-6 whitespace-pre-line text-n-slate-12"
      dir="auto"
      data-waslo-template-body
    >
      {{ template.body }}
    </p>

    <div class="flex flex-wrap items-center gap-2 text-xs text-n-slate-10">
      <span>
        <bdi>{{ template.name }}</bdi>
      </span>
      <span aria-hidden="true">·</span>
      <span>{{ template.language }}</span>
      <span aria-hidden="true">·</span>
      <span>{{ blanks }}</span>
      <span
        v-if="isMarketing"
        class="inline-flex items-center h-5 px-2 rounded-md bg-n-amber-3 text-n-amber-11"
      >
        {{ t('WASLO.BROADCASTS.COMPOSE.MESSAGE.MARKETING') }}
      </span>
    </div>

    <p
      v-if="!template.sendable"
      class="mb-0 text-sm text-n-amber-11"
      data-waslo-template-unsendable
    >
      {{ template.unsendableReason }}
    </p>

    <button
      v-else
      type="button"
      class="inline-flex items-center self-start h-8 px-3 text-sm font-medium rounded-lg"
      :class="
        isChosen
          ? 'bg-n-blue-3 text-n-blue-11'
          : 'bg-n-alpha-2 text-n-slate-12 hover:bg-n-slate-4'
      "
      data-waslo-template-choose
      @click="choose"
    >
      {{
        isChosen
          ? t('WASLO.BROADCASTS.COMPOSE.MESSAGE.CHOSEN')
          : t('WASLO.BROADCASTS.COMPOSE.MESSAGE.CHOOSE')
      }}
    </button>
  </div>
</template>
